import clsx from 'clsx'
import Typography from 'components/Common/Typography'
import NumberInput from 'components/NumberInput/NumberInput'
import { INPUT_LABEL_SEPARATOR, PERCENTAGE_SUGGESTIONS } from 'config/ui'
import { ChangeEvent, memo, ReactNode, useRef, useState } from 'react'
import { cn } from 'utils/classnames'

type Props = {
  topLeftLabel: string
  topLeftValue?: string
  topRightLabel?: string | ReactNode
  topRightValue?: ReactNode | string
  onClickTopRightLabel?: () => void
  inputValue?: number | string
  onInputValueChange?: (e: ChangeEvent<HTMLInputElement>) => void
  onClickMax?: () => void
  onFocus?: () => void
  onBlur?: () => void
  showMaxButton?: boolean
  staticInput?: boolean
  children?: ReactNode
  showPercentSelector?: boolean
  onPercentChange?: (percentage: number) => void
  containerClass?: string
  inputContainerClass?: string
  placeholder?: string
  percentSelectorAlwaysVisible?: boolean
  allowNegative?: boolean // NEW PROP ADDED
}

function BuyInputSection(props: Props) {
  const {
    topLeftLabel,
    topLeftValue,
    topRightLabel,
    topRightValue,
    // onClickTopRightLabel,
    inputValue,
    onInputValueChange,
    onClickMax,
    onFocus,
    onBlur,
    showMaxButton,
    staticInput,
    children,
    showPercentSelector,
    onPercentChange,
    inputContainerClass,
    // containerClass,
    placeholder,
    percentSelectorAlwaysVisible = false,
    allowNegative = false, // DEFAULT TO FALSE, preserving original non-negative validation
  } = props

  const [, setIsPercentSelectorVisible] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  function handleOnFocus() {
    if (showPercentSelector && onPercentChange) {
      setIsPercentSelectorVisible(true)
    }
    onFocus?.()
  }

  function handleOnBlur() {
    if (showPercentSelector && onPercentChange) {
      setIsPercentSelectorVisible(false)
    }
    onBlur?.()
  }

  function handleBoxClick() {
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }

  function onUserInput(e: ChangeEvent<HTMLInputElement>) {
    let val = e.target.value
    let sign = ''

    if (allowNegative) {
      // 1. Check and store the sign
      if (val.startsWith('-')) {
        sign = '-'
        val = val.substring(1) // Remove sign for validation and decimal trimming
      }
    }

    // 2. Validate the non-negative part: only digits and one optional dot
    // This maintains the original decimal and character validation logic
    if (!/^\d*\.?\d*$/.test(val)) {
      return
    }

    if (val.includes('.')) {
      const [intPart, decimalPart = ''] = val.split('.')

      if (intPart === '0') {
        // Count significant non-zero digits after leading zeros
        let significantCount = 0
        let allowedDecimals = ''

        for (let ch of decimalPart) {
          allowedDecimals += ch
          if (ch !== '0') {
            significantCount++
          }
          if (significantCount >= 2) {
            break // stop after 2 meaningful digits
          }
        }

        val = `${intPart}.${allowedDecimals}`
      } else {
        // Normal numbers → only allow 2 decimals
        val = `${intPart}.${decimalPart.slice(0, 2)}`
      }
    }

    // Re-add the sign if it was present and the remaining part is valid
    val = sign + val

    // Final check to prevent just a solitary '-' if not allowed (and the default is false)
    if (val === '-' && !allowNegative) {
      return
    }

    if (onInputValueChange) {
      const event = {
        ...e,
        target: { ...e.target, value: val },
      } as ChangeEvent<HTMLInputElement>
      onInputValueChange(event)
    }
  }

  function balanceClickHandler() {
    if (showMaxButton && onClickMax) {
      onClickMax()
    }
  }

  return (
    <div className="w-full">
      <div onClick={handleBoxClick}>
        <div className="mb-2 mt-3 flex items-center justify-between">
          <span className="flex items-center gap-1 font-body text-xs font-medium uppercase text-th-fgd-3 fhd:text-sm">
            {topLeftLabel}
            {topLeftValue && (
              <>
                {INPUT_LABEL_SEPARATOR}
                <Typography font="font-number">{` ${topLeftValue}`}</Typography>
              </>
            )}
          </span>
          <span className="Exchange-swap-label flex items-center">
            <span className="font-body text-xs font-medium text-th-fgd-3 fhd:text-sm">
              {topRightLabel || ''}
            </span>
            {topRightLabel ? INPUT_LABEL_SEPARATOR : ''}&nbsp;
            <span
              className={clsx(
                'text-th-fgd-1',
                showMaxButton && 'cursor-pointer',
              )}
              onClick={balanceClickHandler}
            >
              <Typography font="font-number">{topRightValue || ''}</Typography>
            </span>
            {showMaxButton && topRightValue && (
              <button
                type="button"
                className="ml-1 text-th-fgd-3"
                onClick={onClickMax}
              >
                MAX
              </button>
            )}
          </span>
        </div>
        <div
          className={cn(
            'flex w-full items-center justify-between rounded-md',
            'bg-patek-neutral-celestial-grey-900',
            'border border-patek-neutral-celestial-grey-800 p-2',
            'font-body text-xs font-bold text-th-fgd-1 fhd:text-sm',
            'placeholder:!text-th-fgd-1',
            'focus:outline-none',
            '!h-[32px] lg:text-base',
            inputContainerClass,
          )}
        >
          {!staticInput && (
            <NumberInput
              value={inputValue}
              className="!h-8 w-full bg-patek-neutral-celestial-grey-900 font-number text-xs focus:outline-none fhd:text-sm"
              inputRef={inputRef}
              onValueChange={onUserInput}
              onFocus={handleOnFocus}
              onBlur={handleOnBlur}
              placeholder={placeholder || '0.0'}
            />
          )}
          {staticInput && (
            <div className="w-full flex-1 text-lg text-th-fgd-1">
              <Typography font="font-number">{inputValue}</Typography>
            </div>
          )}
          <div className="flex h-full flex-shrink-0 items-center justify-center whitespace-nowrap text-xs font-medium text-th-fgd-1 fhd:text-sm">
            {children}
          </div>
        </div>
      </div>
      {percentSelectorAlwaysVisible && (
        <ul
          className="absolute mt-0 flex list-none rounded-sm p-0 backdrop-blur"
          style={{
            background:
              'linear-gradient(270deg, rgb(24, 24, 24) 0%, rgb(24, 24, 24) 100%)',
            boxShadow: '0px 4px 14px rgba(0, 0, 0, 0.25)',
          }}
        >
          {PERCENTAGE_SUGGESTIONS.map((percentage) => (
            <li
              className="w-12 cursor-pointer rounded-sm px-3 py-2 text-center text-xs hover:bg-[#b4bbff1a] fhd:text-sm"
              key={percentage}
              onMouseDown={() => {
                onPercentChange?.(percentage)
                handleOnBlur()
              }}
            >
              {percentage}%
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export default memo(BuyInputSection)
